import { useCallback, useEffect, useRef, useState } from "react";
import { listClients } from "@/features/clients/api";
import type { Client, ClientListMeta, ClientListParams } from "@/features/clients/types";
import { ApiError } from "@/lib/api";

const EMPTY_META: ClientListMeta = { count: 0, next: null, previous: null };

interface UseClientsResult {
  clients: Client[];
  meta: ClientListMeta;
  loading: boolean;
  error: string | null;
  params: ClientListParams;
  /** Merge a partial update into the query params (triggers a refetch). */
  setParams: (patch: Partial<ClientListParams>) => void;
  refetch: () => void;
}

/**
 * Owns the clients-list query params and fetches a page whenever they change.
 *
 * Two guards keep this from over-fetching:
 * - `lastKeyRef` skips a fetch when the effective params are unchanged, which
 *   also collapses React StrictMode's dev-only double-invoke into one request.
 * - `requestIdRef` applies only the latest response, so a slow earlier request
 *   can't clobber a newer one (out-of-order responses).
 */
export function useClients(initial: ClientListParams = {}): UseClientsResult {
  const [params, setParamsState] = useState<ClientListParams>({
    page: 1,
    pageSize: 25,
    ...initial,
  });
  const [clients, setClients] = useState<Client[]>([]);
  const [meta, setMeta] = useState<ClientListMeta>(EMPTY_META);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const lastKeyRef = useRef<string | null>(null);
  const requestIdRef = useRef(0);
  const mountedRef = useRef(true);

  const setParams = useCallback((patch: Partial<ClientListParams>) => {
    setParamsState((prev) => ({ ...prev, ...patch }));
  }, []);

  const refetch = useCallback(() => setReloadKey((k) => k + 1), []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  useEffect(() => {
    const key = `${reloadKey}|${JSON.stringify(params)}`;
    // Identical params (incl. StrictMode's second invoke) → don't refetch.
    if (lastKeyRef.current === key) return;
    lastKeyRef.current = key;

    const reqId = ++requestIdRef.current;
    setLoading(true);
    setError(null);

    listClients(params)
      .then((result) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setClients(result.clients);
        setMeta(result.meta);
      })
      .catch((err) => {
        if (!mountedRef.current || requestIdRef.current !== reqId) return;
        setClients([]);
        setMeta(EMPTY_META);
        setError(err instanceof ApiError ? err.message : "Failed to load clients.");
      })
      .finally(() => {
        if (mountedRef.current && requestIdRef.current === reqId) setLoading(false);
      });
  }, [params, reloadKey]);

  return { clients, meta, loading, error, params, setParams, refetch };
}
